import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { newBaseMap, projectFor, type BaseProvider } from "@/lib/amap";
import L from "leaflet";
import {
  routes as mockRoutes,
  routePoints as mockRoutePoints,
  type Direction,
  type Route,
  type RoutePoint,
} from "@/mocks/routes";
import { scenes as mockScenes, type Scene } from "@/mocks/scenes";
import { useApi } from "@/hooks/useApi";
import { apiSend, apiGet } from "@/lib/api";
import { toast, confirmDialog } from "@/components/ui/feedback";
import { RouteEditorModal, StatusBadge } from "../page";
import {
  type PointRow,
  type PointVariantCells,
  type SegmentPart,
  type SegmentVariant,
  VARIANT_LABELS,
  VARIANT_ORDER,
  exportPointsToExcel,
  downloadTemplate,
  parseExcelFile,
} from "./excel-io";

interface SegmentRow {
  part: SegmentPart;
  variant?: SegmentVariant;
  script: string;
  pauseSec: number;
}

function emptyVariantCells(): PointVariantCells {
  return {
    segment1: { script: "", pauseSec: 0 },
    segment2: { script: "", pauseSec: 0 },
    question: { script: "", pauseSec: 0 },
    answerYes: { script: "", pauseSec: 0 },
    answerNo: { script: "", pauseSec: 0 },
  };
}

function partToCellKey(p: SegmentPart): keyof PointVariantCells {
  switch (p) {
    case "segment_1": return "segment1";
    case "segment_2": return "segment2";
    case "question":  return "question";
    case "answer_yes": return "answerYes";
    case "answer_no":  return "answerNo";
  }
}

// 与点位详情页的多角色 × 多语言矩阵保持一致
const BULK_TRACK_VOICES = ["鲟侠船长", "金梅导游", "小船长"];
const BULK_TRACK_LANGS: [string, string][] = [
  ["zh", "中文"], ["en", "英语"], ["ja", "日语"], ["ko", "韩语"], ["ru", "俄语"],
  ["fr", "法语"], ["es", "西语"], ["de", "德语"], ["it", "意语"], ["ar", "阿语"],
];
const BULK_PARTS: [SegmentPart, string][] = [
  ["segment_1", "第1段·引入"],
  ["segment_2", "第2段·主讲解"],
  ["question", "第3段·互动问题"],
  ["answer_yes", "第4段·肯定回答"],
  ["answer_no", "第4段·否定回答"],
];

function RouteDetailPage() {
  const { routeId = "" } = useParams<{ routeId: string }>();
  const [searchParams] = useSearchParams();

  // Route 本身：live + mock fallback
  const mockRoute = mockRoutes.find((r) => r.id === routeId);
  const { data: route, reload: reloadRoute } = useApi<Route | null>(`/routes/${routeId}`, (mockRoute ?? null) as Route | null);

  // Route 下点位列表（远程 + mock fallback）
  const mockPointsAll = useMemo(
    () => mockRoutePoints.filter((p) => p.routeId === routeId),
    [routeId],
  );
  const { data: allPoints, reload: reloadPoints } = useApi<RoutePoint[]>(
    `/routes/${routeId}/points`,
    mockPointsAll,
  );

  // scenes pool — 反查点位详细字段（位置/半径等），完全复用现有 /scenes 接口
  const { data: scenes, reload: reloadScenes } = useApi<Scene[]>("/scenes", mockScenes as unknown as Scene[]);
  const sceneById = useMemo(() => {
    const m = new Map<string, Scene>();
    for (const s of scenes) m.set(String(s.id), s);
    return m;
  }, [scenes]);

  const upAvail = allPoints.some((p) => p.direction === "up");
  const downAvail = allPoints.some((p) => p.direction === "down");
  // 默认方向严格按 URL ?direction=… 决定，否则一律落到"上行"。
  // 避免之前 useState 用 stale mock fallback 计算导致"上行点进去看到下行"的 bug。
  const initialDir: Direction = searchParams.get("direction") === "down" ? "down" : "up";
  const [direction, setDirection] = useState<Direction>(initialDir);

  const points = useMemo(
    () => allPoints.filter((p) => p.direction === direction).sort((a, b) => a.seq - b.seq),
    [allPoints, direction],
  );

  // 编辑点位基本信息（名称 / 位置 / 触发半径） —— 通过游线表行内的笔图标进入
  const [editingScene, setEditingScene] = useState<Scene | null>(null);

  // 一键切换"标准点位" —— 复用 PUT /scenes/{id}，只翻转 isStandard，其它字段原样回写
  const toggleStandard = async (sc: Scene) => {
    try {
      const next = !sc.isStandard;
      await apiSend("PUT", `/scenes/${sc.id}`, {
        name: sc.name,
        description: sc.description,
        lat: sc.lat,
        lng: sc.lng,
        triggerRadius: sc.triggerRadius,
        visibility: sc.visibility,
        voice: sc.voice,
        status: sc.status,
        groupId: sc.groupId ?? "",
        isStandard: next,
        audioStatus: sc.audioStatus,
        knowledgeCount: sc.knowledgeCount,
        imageCount: sc.imageCount,
        recognitionHint: sc.recognitionHint ?? "",
        coverUrl: sc.coverUrl ?? "",
        tags: sc.tags ?? [],
        triggerMode: sc.triggerMode,
        triggerHeadingDeg: sc.triggerHeadingDeg,
        triggerWidthM: sc.triggerWidthM,
      });
      toast(next ? `「${sc.name}」已加入标准点位` : `「${sc.name}」已移出标准点位`, "success");
      reloadScenes();
    } catch (e) {
      toast(`切换失败: ${e instanceof Error ? e.message : e}`, "error");
    }
  };
  // 添加点位
  const [showAdd, setShowAdd] = useState(false);
  // 从点位库批量导入
  const [showImport, setShowImport] = useState(false);
  // 编辑游线
  const [showEditRoute, setShowEditRoute] = useState(false);

  // Excel 导入/导出
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [excelBusy, setExcelBusy] = useState<"" | "export" | "import" | "template">("");

  // 一键生成全部音频
  const [showBulkAudio, setShowBulkAudio] = useState(false);

  // 触发区域地图
  const [showTriggerMap, setShowTriggerMap] = useState(false);

  const handleExport = async () => {
    if (!route) return;
    if (points.length === 0) {
      toast("当前方向没有点位可导出", "info");
      return;
    }
    setExcelBusy("export");
    try {
      // 并发拉每个场景在 3 个 variant 下的全部 5 段；没数据的段保留空槽位。
      const rows: PointRow[] = await Promise.all(
        points.map(async (p) => {
          const sc = sceneById.get(p.sceneId);
          const variants: Record<SegmentVariant, PointVariantCells> = {
            concise: emptyVariantCells(),
            story: emptyVariantCells(),
            talky: emptyVariantCells(),
          };
          if (sc) {
            await Promise.all(
              VARIANT_ORDER.map(async (v) => {
                try {
                  const data = await apiGet<SegmentRow[]>(`/scenes/${sc.id}/segments?variant=${v}`);
                  for (const s of data) {
                    const key = partToCellKey(s.part);
                    variants[v][key] = {
                      script: s.script ?? "",
                      pauseSec: Number(s.pauseSec) || 0,
                    };
                  }
                } catch { /* 单 variant 失败不阻塞其它两套 */ }
              }),
            );
          }
          return {
            seq: p.seq,
            name: sc?.name ?? "(点位缺失)",
            lat: sc?.lat ?? 0,
            lng: sc?.lng ?? 0,
            triggerRadius: sc?.triggerRadius ?? 0,
            triggerMode: sc?.triggerMode ?? "circle",
            triggerHeadingDeg: sc?.triggerHeadingDeg ?? null,
            triggerWidthM: sc?.triggerWidthM ?? null,
            variants,
          };
        }),
      );
      await exportPointsToExcel(route.name, direction, rows);
      toast(`已导出 ${rows.length} 个点位（含 3 套讲解风格）`, "success");
    } catch (e) {
      toast(`导出失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally { setExcelBusy(""); }
  };

  const handleTemplate = async () => {
    setExcelBusy("template");
    try {
      await downloadTemplate();
      toast("模板已下载，填好后用「导入」上传", "success");
    } catch (e) {
      toast(`模板下载失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally { setExcelBusy(""); }
  };

  const handleImportFile = async (file: File) => {
    if (!route) return;
    setExcelBusy("import");
    try {
      const rows = await parseExcelFile(file);
      if (rows.length === 0) {
        toast("文件里没有可识别的点位行（请按模板格式填写）", "error");
        return;
      }
      // 估算总段数 = 行 × 3 套 × 5 段中"有内容/有间隔"的条目
      const totalSegs = rows.reduce((acc, r) => {
        let n = 0;
        for (const v of VARIANT_ORDER) {
          const cells = r.variants[v];
          if (cells.segment1.script || cells.segment1.pauseSec) n++;
          if (cells.segment2.script || cells.segment2.pauseSec) n++;
          if (cells.question.script  || cells.question.pauseSec)  n++;
          if (cells.answerYes.script || cells.answerYes.pauseSec) n++;
          if (cells.answerNo.script  || cells.answerNo.pauseSec)  n++;
        }
        return acc + n;
      }, 0);
      const ok = await confirmDialog({
        title: `导入 ${rows.length} 个点位？`,
        message:
          `将向 "${route.name}" 的${direction === "up" ? "上行" : "下行"}方向追加 ${rows.length} 个点位。\n` +
          `每行会新建一个场景，并按文件中"顺序"列升序追加到现有点位末尾。\n` +
          `工作簿的 3 个 sheet（${VARIANT_ORDER.map((v) => VARIANT_LABELS[v]).join(" / ")}）会各自写入对应 variant；` +
          `5 段中有内容/有间隔的会写入，空段不写。预计写入 ${totalSegs} 个段落。\n` +
          `已有点位不会被删除或修改。`,
        confirmText: "开始导入",
        danger: false,
      });
      if (!ok) return;
      let nextSeq = points.at(-1)?.seq ?? 0;
      let ok2 = 0;
      let failed = 0;
      // 顺序执行避免后端 seq 冲突
      const partOrder: SegmentPart[] = ["segment_1", "segment_2", "question", "answer_yes", "answer_no"];
      for (const r of rows) {
        nextSeq++;
        try {
          const scene = await apiSend<Scene>("POST", "/scenes", {
            name: r.name,
            description: "",
            lat: r.lat,
            lng: r.lng,
            triggerRadius: r.triggerRadius,
            voice: "",
            triggerMode: r.triggerMode ?? "circle",
            triggerHeadingDeg: r.triggerHeadingDeg ?? null,
            triggerWidthM: r.triggerWidthM ?? null,
          });
          await apiSend("POST", `/routes/${route.id}/points`, {
            direction,
            seq: nextSeq,
            sceneId: String(scene.id),
            script: "",
            voice: "",
            pauseAfterSec: 3,
            audioStatus: "none",
            audioUrl: "",
          });
          // 把 3 套 variant × 5 段中"有内容或有间隔"的写入对应 segment
          for (const v of VARIANT_ORDER) {
            const cells = r.variants[v];
            for (const part of partOrder) {
              const cell = cells[partToCellKey(part)];
              if (!cell.script && !cell.pauseSec) continue;
              await apiSend("PUT", `/scenes/${scene.id}/segments/${part}?variant=${v}`, {
                script: cell.script,
                pauseSec: cell.pauseSec,
                voice: "",
              });
            }
          }
          ok2++;
        } catch (err) {
          failed++;
          console.error("import row failed", r, err);
        }
      }
      reloadPoints();
      reloadScenes();
      if (failed === 0) toast(`已导入 ${ok2} 个点位`, "success");
      else toast(`导入完成：成功 ${ok2}，失败 ${failed}（详见控制台）`, "error");
    } catch (e) {
      toast(`导入失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally { setExcelBusy(""); }
  };

  if (!route) {
    return (
      <div className="space-y-5">
        <div className="text-sm">
          <Link to="/knowledge/routes" className="text-teal-600 hover:text-teal-700">
            <i className="ri-arrow-left-line mr-1" />返回游线列表
          </Link>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <i className="ri-error-warning-line text-3xl text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">未找到该游线</p>
        </div>
      </div>
    );
  }

  // Tab 始终可点击 —— 空方向也允许进入以便添加第一个点位
  const dirTabs: { key: Direction; label: string; icon: string; hasPoints: boolean }[] = [
    { key: "up", label: "上行", icon: "ri-arrow-up-line", hasPoints: upAvail },
    { key: "down", label: "下行", icon: "ri-arrow-down-line", hasPoints: downAvail },
  ];

  return (
    <div className="space-y-5">
      {/* 顶部 */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-sm mb-2">
            <Link to="/knowledge/routes" className="text-teal-600 hover:text-teal-700">
              <i className="ri-arrow-left-line mr-1" />游线列表
            </Link>
          </div>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">{route.name}</h1>
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-teal-50 text-teal-700">{route.shortName}</span>
            <StatusBadge status={route.status} />
          </div>
          <p className="text-sm text-gray-500">{route.description}</p>
          <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
            <span><i className="ri-time-line mr-0.5" />{route.durationLabel}</span>
            <span><i className="ri-map-pin-line mr-0.5" />{route.sceneCount} 点位</span>
          </div>
        </div>
        <button
          onClick={() => setShowEditRoute(true)}
          className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
        >
          <i className="ri-edit-line mr-1.5" />编辑游线
        </button>
      </div>

      {/* 上 / 下行 Tab */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-1 flex items-center gap-1 w-fit">
        {dirTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setDirection(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              direction === t.key
                ? "bg-teal-600 text-white shadow-sm"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            }`}
          >
            <i className={`${t.icon} text-base`} />
            {t.label}
            {!t.hasPoints && <span className={`text-xs ${direction === t.key ? "text-white/70" : "text-gray-400"}`}>(空)</span>}
          </button>
        ))}
        <div className="ml-2 pl-3 border-l border-gray-200 text-xs text-gray-500 pr-2">
          当前方向 {points.length} 个点位 · 讲解词独立维护
        </div>
      </div>

      {/* 点位列表（按方向顺序） */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-gray-500">
            <i className={`${direction === "up" ? "ri-arrow-up-line" : "ri-arrow-down-line"} mr-1`} />
            {direction === "up" ? "上行" : "下行"} · 共 {points.length} 个点位
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => setShowTriggerMap(true)}
              disabled={allPoints.length === 0}
              title={allPoints.length === 0 ? "游线暂无点位" : "查看触发区域地图"}
              className="px-2.5 py-1.5 border border-gray-200 text-gray-600 rounded-md text-xs font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-map-2-line mr-1" />触发区域
            </button>
            <button
              onClick={handleTemplate}
              disabled={!!excelBusy}
              title="下载 Excel 导入模板（含示例行 / 宋体字体）"
              className="px-2.5 py-1.5 border border-gray-200 text-gray-600 rounded-md text-xs font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-file-download-line mr-1" />{excelBusy === "template" ? "生成中…" : "模板"}
            </button>
            <button
              onClick={handleExport}
              disabled={!!excelBusy || points.length === 0}
              title={points.length === 0 ? "当前方向没有点位可导出" : `一键导出当前方向 ${points.length} 个点位为 Excel`}
              className="px-2.5 py-1.5 border border-teal-200 text-teal-700 rounded-md text-xs font-medium hover:bg-teal-50 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-file-excel-2-line mr-1" />{excelBusy === "export" ? "导出中…" : "导出 Excel"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) handleImportFile(f);
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={!!excelBusy}
              title="上传按模板填好的 Excel，按顺序追加到当前方向末尾"
              className="px-2.5 py-1.5 border border-amber-200 text-amber-700 rounded-md text-xs font-medium hover:bg-amber-50 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-file-upload-line mr-1" />{excelBusy === "import" ? "导入中…" : "导入 Excel"}
            </button>
            <button
              onClick={() => setShowBulkAudio(true)}
              disabled={points.length === 0}
              title={points.length === 0 ? "当前方向没有点位" : `一键为当前方向 ${points.length} 个点位的全部讲解词批量生成多角色多语言音频`}
              className="px-2.5 py-1.5 border border-violet-200 text-violet-700 rounded-md text-xs font-medium hover:bg-violet-50 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-magic-line mr-1" />一键生成音频
            </button>
            <button
              onClick={() => setShowImport(true)}
              title="从全局点位库挑选已有点位，一次性批量加入当前方向"
              className="px-2.5 py-1.5 border border-teal-300 text-teal-700 rounded-md text-xs font-medium hover:bg-teal-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-import-line mr-1" />从点位库导入
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="px-3 py-1.5 bg-teal-600 text-white rounded-md text-xs font-medium hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line mr-1" />新建点位
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">顺序</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">点位名称</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">位置</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">触发区域</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">讲解音频</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">知识块</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {points.map((p) => {
                const sc = sceneById.get(p.sceneId);
                // 讲解音频/状态等都直接读 scene 字段，列含义与 /knowledge/scenes 完全对齐
                const audioCfg = sc ? ({
                  generated: { label: "已生成", color: "bg-emerald-100 text-emerald-700" },
                  pending:   { label: "待生成", color: "bg-amber-100 text-amber-700" },
                  none:      { label: "未生成", color: "bg-gray-100 text-gray-700" },
                }[sc.audioStatus] ?? { label: sc.audioStatus, color: "bg-gray-100 text-gray-700" })
                  : { label: "—", color: "bg-gray-100 text-gray-700" };
                return (
                  <tr key={`${p.routeId}-${p.direction}-${p.seq}`} className="hover:bg-teal-50/30 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono text-gray-500">{p.seq}</span>
                    </td>
                    <td className="px-4 py-3">
                      {sc ? (
                        <Link
                          to={`/knowledge/scenes/${sc.id}`}
                          className="group block"
                          title="进入点位内容"
                        >
                          <div className="text-sm font-medium text-gray-900 group-hover:text-teal-700 group-hover:underline">
                            {sc.name}
                            {sc.isStandard && (
                              <span className="ml-1 text-amber-500" title="标准点位 · 可被其它游线导入">★</span>
                            )}
                          </div>
                          {sc.description && <div className="text-xs text-gray-400 mt-0.5 line-clamp-1 group-hover:text-gray-500">{sc.description}</div>}
                        </Link>
                      ) : (
                        <span className="text-sm font-medium text-red-500">点位缺失</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {sc ? (
                        <div className="text-xs text-gray-600 font-mono whitespace-nowrap">
                          {sc.lat.toFixed(4)}, {sc.lng.toFixed(4)}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {sc ? <RadiusCell scene={sc} /> : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${audioCfg.color} whitespace-nowrap`}>
                        {audioCfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{sc?.knowledgeCount ?? 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => sc && toggleStandard(sc)}
                          disabled={!sc}
                          title={sc?.isStandard ? "已是标准点位 — 点击移出" : "设为标准点位 — 可被其它游线导入"}
                          className={`w-7 h-7 flex items-center justify-center rounded transition-colors disabled:opacity-30 ${
                            sc?.isStandard
                              ? "text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                              : "text-gray-300 hover:text-amber-500 hover:bg-amber-50"
                          }`}
                        >
                          <i className={sc?.isStandard ? "ri-star-fill" : "ri-star-line"} />
                        </button>
                        <button
                          onClick={() => sc && setEditingScene(sc)}
                          disabled={!sc}
                          title="编辑点位（名称 / 位置 / 触发区域）"
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors disabled:opacity-30"
                        >
                          <i className="ri-edit-line" />
                        </button>
                        <button
                          onClick={async () => {
                            const ok = await confirmDialog({
                              title: "删除该点位？",
                              message: `将从${direction === "up" ? "上行" : "下行"}游线中移除「${sc?.name ?? "未命名"}」（顺序 ${p.seq}）。\n点位本身（讲解词 / 知识 / 音频）会保留在点位库中，只是不再属于本游线。`,
                              confirmText: "删除",
                              danger: true,
                            });
                            if (!ok) return;
                            try {
                              await apiSend("DELETE", `/routes/${route.id}/points/${p.direction}/${p.seq}`);
                              toast("已从游线中移除该点位", "success");
                              reloadPoints();
                            } catch (e) {
                              toast(`删除失败: ${e instanceof Error ? e.message : e}`, "error");
                            }
                          }}
                          title="从该游线移除此点位"
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {points.length === 0 && (
          <div className="px-4 py-12 text-center">
            <i className="ri-map-pin-add-line text-4xl text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 mb-3">本方向暂无点位</p>
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setShowImport(true)}
                className="px-3 py-1.5 border border-teal-300 text-teal-700 rounded-md text-xs font-medium hover:bg-teal-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-import-line mr-1" />从点位库导入
              </button>
              <button
                onClick={() => setShowAdd(true)}
                className="px-3 py-1.5 bg-teal-600 text-white rounded-md text-xs font-medium hover:bg-teal-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line mr-1" />新建第一个点位
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 方向化讲解词编辑 */}
      {editingScene && (
        <EditScenePointModal
          scene={editingScene}
          onClose={() => setEditingScene(null)}
          onSaved={() => { setEditingScene(null); reloadPoints(); reloadScenes(); }}
        />
      )}

      {/* 编辑游线（含 保存/发布/下线 三按钮） */}
      {showEditRoute && (
        <RouteEditorModal
          mode="edit"
          route={route}
          onClose={() => setShowEditRoute(false)}
          onSaved={() => { setShowEditRoute(false); reloadRoute(); }}
        />
      )}

      {/* 添加点位 —— 超简版：只填名字其它走默认值，符合"添加点位先快速占位"的运营节奏 */}
      {showAdd && (
        <QuickAddPointModal
          routeShortName={route.shortName}
          direction={direction}
          nextSeq={(points.at(-1)?.seq ?? 0) + 1}
          onClose={() => setShowAdd(false)}
          onCreated={async (sceneId, name) => {
            const nextSeq = (points.at(-1)?.seq ?? 0) + 1;
            await apiSend("POST", `/routes/${route.id}/points`, {
              direction,
              seq: nextSeq,
              sceneId,
              script: "",
              voice: "",
              pauseAfterSec: 3,
              audioStatus: "none",
              audioUrl: "",
            });
            toast(`已新建点位「${name}」并加入${direction === "up" ? "上行" : "下行"}第 ${nextSeq} 位`, "success");
            setShowAdd(false);
            reloadPoints();
            reloadScenes();
          }}
        />
      )}

      {/* 一键生成全部音频 */}
      {showBulkAudio && (
        <BulkAudioModal
          routeName={route.name}
          direction={direction}
          points={points}
          sceneById={sceneById}
          onClose={() => setShowBulkAudio(false)}
        />
      )}

      {/* 从点位库批量导入 */}
      {showImport && (
        <ImportPointsModal
          routeId={route.id}
          routeName={route.name}
          direction={direction}
          startSeq={(points.at(-1)?.seq ?? 0) + 1}
          existingSceneIds={new Set(allPoints.filter(p => p.direction === direction).map(p => p.sceneId))}
          allScenes={scenes}
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); reloadPoints(); }}
        />
      )}

      {/* 触发区域地图可视化 */}
      {showTriggerMap && (
        <TriggerAreaMapModal
          routeName={route.name}
          currentDirection={direction}
          allPoints={allPoints}
          sceneById={sceneById}
          onClose={() => setShowTriggerMap(false)}
        />
      )}
    </div>
  );
}

// ImportPointsModal —— 从全局点位库挑已有点位批量加入当前方向。
// 多选 + 搜索 + 一键导入。复用 POST /routes/{id}/points 接口，按选择顺序逐个挂上去。
function ImportPointsModal({
  routeId, routeName, direction, startSeq, existingSceneIds, allScenes, onClose, onImported,
}: {
  routeId: string;
  routeName: string;
  direction: Direction;
  startSeq: number;
  existingSceneIds: Set<string>;
  allScenes: Scene[];
  onClose: () => void;
  onImported: () => void;
}) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string[]>([]); // 维持选择顺序 = 导入顺序
  const [hideAlreadyIn, setHideAlreadyIn] = useState(true);
  // 默认只显标准点位 —— 非标准点位是私有的，不应跨游线复用
  const [onlyStandard, setOnlyStandard] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, ok: 0, failed: 0 });

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allScenes.filter((s) => {
      if (hideAlreadyIn && existingSceneIds.has(String(s.id))) return false;
      if (onlyStandard && !s.isStandard) return false;
      if (!q) return true;
      return s.name.toLowerCase().includes(q) || String(s.id).includes(q);
    });
  }, [allScenes, query, hideAlreadyIn, onlyStandard, existingSceneIds]);

  const togglePick = (sid: string) =>
    setPicked((p) => (p.includes(sid) ? p.filter((x) => x !== sid) : [...p, sid]));

  const handleImport = async () => {
    if (picked.length === 0) return;
    setBusy(true);
    setProgress({ done: 0, total: picked.length, ok: 0, failed: 0 });
    let seq = startSeq;
    let ok = 0, failed = 0;
    for (const sid of picked) {
      try {
        await apiSend("POST", `/routes/${routeId}/points`, {
          direction,
          seq,
          sceneId: sid,
          script: "",
          voice: "",
          pauseAfterSec: 3,
          audioStatus: "none",
          audioUrl: "",
        });
        seq++; ok++;
      } catch (e) {
        failed++;
        console.error("import point failed", sid, e);
      } finally {
        setProgress((p) => ({ ...p, done: p.done + 1, ok, failed }));
      }
    }
    setBusy(false);
    if (failed === 0) toast(`已导入 ${ok} 个点位到「${routeName}」${direction === "up" ? "上行" : "下行"}`, "success");
    else toast(`导入完成：成功 ${ok}，失败 ${failed}`, "info");
    onImported();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-[800px] max-w-full max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* 头部 */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              <i className="ri-import-line mr-1 text-teal-600" />从点位库导入到「{routeName}」{direction === "up" ? "上行" : "下行"}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              多选挑选已有点位，按勾选顺序追加到当前方向末尾（从第 {startSeq} 位开始）。已在本方向的点位默认隐藏。
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* 搜索 + 过滤 */}
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索点位名称或 ID"
              className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              autoFocus
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
            <input type="checkbox" checked={onlyStandard} onChange={(e) => setOnlyStandard(e.target.checked)} className="accent-teal-600" />
            只显标准点位
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
            <input type="checkbox" checked={hideAlreadyIn} onChange={(e) => setHideAlreadyIn(e.target.checked)} className="accent-teal-600" />
            隐藏本方向已有
          </label>
          <button
            onClick={() => setPicked(visible.map((s) => String(s.id)))}
            className="text-xs text-teal-600 hover:text-teal-700 whitespace-nowrap"
          >全选可见</button>
          <button onClick={() => setPicked([])} className="text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap">清空</button>
        </div>

        {/* 列表 */}
        <div className="flex-1 overflow-y-auto">
          {visible.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">
              <i className="ri-inbox-line text-3xl block mb-2 text-gray-300" />
              {query ? "没有匹配的点位" : "点位库为空"}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {visible.map((s) => {
                const sid = String(s.id);
                const isPicked = picked.includes(sid);
                const order = isPicked ? picked.indexOf(sid) + 1 : 0;
                return (
                  <li
                    key={sid}
                    onClick={() => togglePick(sid)}
                    className={`px-5 py-2.5 flex items-center gap-3 cursor-pointer transition-colors ${
                      isPicked ? "bg-teal-50/60" : "hover:bg-gray-50"
                    }`}
                  >
                    <input type="checkbox" checked={isPicked} onChange={() => togglePick(sid)} className="accent-teal-600" />
                    {isPicked && (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-600 text-white text-xs font-medium shrink-0">
                        {order}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{s.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        ID {sid} · {s.lat.toFixed(4)}, {s.lng.toFixed(4)} · {s.triggerRadius}m
                        {existingSceneIds.has(sid) && <span className="ml-2 text-amber-600">· 已在本方向</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400 shrink-0">
                      <span>知识 {s.knowledgeCount}</span>
                      <span className={`px-1.5 py-0.5 rounded-full ${
                        s.audioStatus === "generated" ? "bg-emerald-100 text-emerald-700"
                        : s.audioStatus === "pending" ? "bg-amber-100 text-amber-700"
                        : "bg-gray-100 text-gray-600"
                      }`}>
                        {s.audioStatus === "generated" ? "音频已生成" : s.audioStatus === "pending" ? "待生成" : "无音频"}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* 底部 */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {busy ? (
              <span>导入中… {progress.done} / {progress.total}（成功 {progress.ok}{progress.failed > 0 ? `，失败 ${progress.failed}` : ""}）</span>
            ) : (
              <span>已选 <strong className="text-teal-700">{picked.length}</strong> 个</span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={busy} className="px-4 py-1.5 bg-gray-100 text-gray-600 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-60">取消</button>
            <button
              onClick={handleImport}
              disabled={busy || picked.length === 0}
              className="px-4 py-1.5 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700 disabled:opacity-60"
            >
              {busy ? "导入中…" : `导入 ${picked.length} 个`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// BulkAudioModal —— 给当前方向所有点位的 5 段讲解词批量生成音频。
// 选项与点位详情页的"多角色 × 多语言"矩阵保持一致：
// 1) 选角色（默认鲟侠船长）/ 2) 选语言（默认中文）/ 3) 选段（默认全选 5 段）。
// 实际只为有 script 的段下发任务，避免无效请求。
function BulkAudioModal({
  routeName,
  direction,
  points,
  sceneById,
  onClose,
}: {
  routeName: string;
  direction: Direction;
  points: RoutePoint[];
  sceneById: Map<string, Scene>;
  onClose: () => void;
}) {
  const [selVoices, setSelVoices] = useState<string[]>(["鲟侠船长"]);
  const [selLangs, setSelLangs] = useState<string[]>(["zh"]);
  const [selParts, setSelParts] = useState<SegmentPart[]>(BULK_PARTS.map(([p]) => p));
  // 状态机：idle → scanning（拉每个 scene 的 segments）→ dispatching（POST 各任务）→ done
  const [phase, setPhase] = useState<"idle" | "scanning" | "dispatching" | "done">("idle");
  const [progress, setProgress] = useState({ done: 0, total: 0, ok: 0, failed: 0 });
  const [planned, setPlanned] = useState<{ scenes: number; tasks: number }>({ scenes: 0, tasks: 0 });

  const toggle = (list: string[], v: string) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

  const togglePart = (p: SegmentPart) =>
    setSelParts((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));

  const start = async () => {
    if (selVoices.length === 0) { toast("至少选择 1 个角色", "info"); return; }
    if (selLangs.length === 0) { toast("至少选择 1 种语言", "info"); return; }
    if (selParts.length === 0) { toast("至少选择 1 段讲解词", "info"); return; }

    // 1) 先扫描：哪些 (scene, part) 有 script、值得下发
    setPhase("scanning");
    type Task = { sceneId: string; sceneName: string; part: SegmentPart };
    const tasks: Task[] = [];
    let scannedScenes = 0;
    for (const p of points) {
      const sc = sceneById.get(p.sceneId);
      if (!sc) continue;
      scannedScenes++;
      try {
        const segs = await apiGet<SegmentRow[]>(`/scenes/${sc.id}/segments`);
        for (const part of selParts) {
          const seg = segs.find((s) => s.part === part);
          if (seg && (seg.script || "").trim()) {
            tasks.push({ sceneId: String(sc.id), sceneName: sc.name, part });
          }
        }
      } catch { /* 单段失败不阻塞 */ }
    }
    setPlanned({ scenes: scannedScenes, tasks: tasks.length });

    if (tasks.length === 0) {
      toast("没有可生成的段（请确认讲解词列已填写）", "info");
      setPhase("idle");
      return;
    }

    const ok = await confirmDialog({
      title: "确认一键生成？",
      message:
        `路线「${routeName}」${direction === "up" ? "上行" : "下行"}方向：` +
        `${scannedScenes} 个点位中有讲解词的段共 ${tasks.length} 个。\n` +
        `每段会生成 ${selVoices.length} 角色 × ${selLangs.length} 语言 = ${selVoices.length * selLangs.length} 条音频，` +
        `合计大约 ${tasks.length * selVoices.length * selLangs.length} 次 TTS 调用。\n` +
        `角色：${selVoices.join("、")}\n语言：${selLangs.map((c) => BULK_TRACK_LANGS.find(([x]) => x === c)?.[1]).join("、")}\n` +
        `任务异步执行，可关闭弹窗，稍后回到列表/点位详情查看进度。`,
      confirmText: "开始生成",
    });
    if (!ok) { setPhase("idle"); return; }

    // 2) 下发：5 并发，后端是异步 (POST 立即 202，后台 goroutine 跑 TTS)
    setPhase("dispatching");
    setProgress({ done: 0, total: tasks.length, ok: 0, failed: 0 });
    let idx = 0;
    const fire = async (t: Task) => {
      try {
        await apiSend("POST", `/scenes/${t.sceneId}/segments/${t.part}/audio/tracks/generate`, {
          voices: selVoices,
          langs: selLangs,
        });
        setProgress((s) => ({ ...s, done: s.done + 1, ok: s.ok + 1 }));
      } catch {
        setProgress((s) => ({ ...s, done: s.done + 1, failed: s.failed + 1 }));
      }
    };
    const CONCURRENCY = 5;
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (idx < tasks.length) {
        const my = idx++;
        await fire(tasks[my]);
      }
    });
    await Promise.all(workers);
    setPhase("done");
  };

  const running = phase === "scanning" || phase === "dispatching";
  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={running ? undefined : onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              <i className="ri-magic-line text-violet-600 mr-1.5" />一键生成全部音频
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {routeName} · {direction === "up" ? "上行" : "下行"} · 共 {points.length} 个点位 · 选一次跑全程
            </p>
          </div>
          <button
            onClick={running ? undefined : onClose}
            disabled={running}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 disabled:opacity-30"
          >
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4 text-sm">
          <div>
            <div className="text-xs font-medium text-gray-700 mb-1.5">角色（多选）</div>
            <div className="flex flex-wrap gap-1.5">
              {BULK_TRACK_VOICES.map((v) => (
                <button
                  key={v}
                  disabled={running}
                  onClick={() => setSelVoices((s) => toggle(s, v))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
                    selVoices.includes(v) ? "bg-teal-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-medium text-gray-700 mb-1.5">语言（多选）</div>
            <div className="flex flex-wrap gap-1.5">
              {BULK_TRACK_LANGS.map(([code, label]) => (
                <button
                  key={code}
                  disabled={running}
                  onClick={() => setSelLangs((s) => toggle(s, code))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
                    selLangs.includes(code) ? "bg-teal-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-medium text-gray-700 mb-1.5">讲解词段（多选）</div>
            <div className="flex flex-wrap gap-1.5">
              {BULK_PARTS.map(([p, label]) => (
                <button
                  key={p}
                  disabled={running}
                  onClick={() => togglePart(p)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
                    selParts.includes(p) ? "bg-teal-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-1.5">
              只会为"实际填了讲解词"的段下发任务，空段会自动跳过。
            </p>
          </div>

          {phase === "scanning" && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
              <i className="ri-search-line mr-1" />正在扫描每个点位的讲解词…
            </div>
          )}
          {(phase === "dispatching" || phase === "done") && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs space-y-1.5">
              <div className="flex items-center justify-between text-gray-600">
                <span>
                  下发进度 {progress.done}/{progress.total}
                  <span className="text-emerald-600 ml-2">成功 {progress.ok}</span>
                  {progress.failed > 0 && <span className="text-red-500 ml-2">失败 {progress.failed}</span>}
                </span>
                <span className="font-mono">{pct}%</span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <p className="text-[11px] text-gray-400">
                {phase === "done"
                  ? "任务已全部下发到后端。实际合成在后台异步执行，可关闭弹窗后回到点位详情试听。"
                  : "后端是异步合成，下发完成后可关闭，稍等几分钟回点位详情即可。"}
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-2 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={running}
            className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
          >
            {phase === "done" ? "完成" : "取消"}
          </button>
          {phase !== "done" && (
            <button
              onClick={start}
              disabled={running}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-60 whitespace-nowrap"
            >
              <i className="ri-magic-line mr-1" />
              {phase === "scanning" ? "扫描中…" : phase === "dispatching" ? "下发中…" : "开始生成"}
            </button>
          )}
        </div>
        {phase === "done" && planned.tasks > 0 && (
          <div className="px-6 pb-4 text-[11px] text-gray-400">
            合计为 {planned.scenes} 个点位、{planned.tasks} 段讲解词下发了 {progress.ok + progress.failed} 个生成任务。
          </div>
        )}
      </div>
    </div>
  );
}

// RadiusCell —— 触发半径/区域只读显示；编辑入口在操作列的笔图标里。
function RadiusCell({ scene }: { scene: Scene }) {
  if (scene.triggerMode === "strip") {
    const heading = scene.triggerHeadingDeg !== undefined && scene.triggerHeadingDeg !== null ? `${scene.triggerHeadingDeg}°` : "—°";
    const width = scene.triggerWidthM !== undefined && scene.triggerWidthM !== null ? `宽${scene.triggerWidthM}m` : "宽—m";
    return (
      <span className="text-sm text-gray-700 whitespace-nowrap">
        条带 {scene.triggerRadius}m · {heading} · {width}
      </span>
    );
  }
  return <span className="text-sm text-gray-700 whitespace-nowrap">圆形 {scene.triggerRadius}m</span>;
}

// EditScenePointModal —— 编辑点位基本信息（名称 / 位置 / 触发半径），
// 只在用户点保存时才落库（不再 inline 自动保存）。
function EditScenePointModal({
  scene,
  onClose,
  onSaved,
}: {
  scene: Scene;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(scene.name);
  const [lat, setLat] = useState(scene.lat);
  const [lng, setLng] = useState(scene.lng);
  const [radius, setRadius] = useState(scene.triggerRadius);
  const [triggerMode, setTriggerMode] = useState(scene.triggerMode ?? "circle");
  const [triggerHeadingDeg, setTriggerHeadingDeg] = useState<number | "">(scene.triggerHeadingDeg ?? "");
  const [triggerWidthM, setTriggerWidthM] = useState<number | "">(scene.triggerWidthM ?? "");
  const [busy, setBusy] = useState(false);

  const dirty =
    name.trim() !== scene.name ||
    Number(lat) !== scene.lat ||
    Number(lng) !== scene.lng ||
    Number(radius) !== scene.triggerRadius ||
    triggerMode !== (scene.triggerMode ?? "circle") ||
    (triggerHeadingDeg === "" ? null : Number(triggerHeadingDeg)) !== (scene.triggerHeadingDeg ?? null) ||
    (triggerWidthM === "" ? null : Number(triggerWidthM)) !== (scene.triggerWidthM ?? null);

  const save = async () => {
    if (!name.trim()) { toast("请填写点位名称", "error"); return; }
    if (triggerMode === "strip") {
      const heading = triggerHeadingDeg !== "" && triggerHeadingDeg !== null ? Number(triggerHeadingDeg) : NaN;
      const width = triggerWidthM !== "" && triggerWidthM !== null ? Number(triggerWidthM) : NaN;
      if (!Number.isFinite(heading)) {
        toast("条带模式朝向角必须为有效数字", "error");
        return;
      }
      if (!Number.isFinite(width) || width <= 0) {
        toast("条带模式宽度必须为大于 0 的数字", "error");
        return;
      }
    }
    setBusy(true);
    try {
      // PUT 是全字段替换 —— 其它字段从当前 scene 带回去，只覆盖我们改的 4 个
      await apiSend("PUT", `/scenes/${scene.id}`, {
        ...scene,
        name: name.trim(),
        lat: Number(lat) || 0,
        lng: Number(lng) || 0,
        triggerRadius: Math.max(0, Math.round(Number(radius) || 0)),
        triggerMode,
        triggerHeadingDeg: triggerMode === "strip" && triggerHeadingDeg !== "" ? Number(triggerHeadingDeg) : null,
        triggerWidthM: triggerMode === "strip" && triggerWidthM !== "" ? Number(triggerWidthM) : null,
      });
      toast("已保存", "success");
      onSaved();
    } catch (e) {
      toast(`保存失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally { setBusy(false); }
  };

  const input = "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h3 className="text-base font-semibold text-gray-900">编辑点位</h3>
            <p className="text-xs text-gray-500 mt-0.5">名称 / 位置 / 触发区域</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">点位名称 <span className="text-red-500">*</span></label>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} className={input} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">纬度 lat</label>
              <input type="number" step="0.0001" value={lat} onChange={(e) => setLat(Number(e.target.value) || 0)} className={input} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">经度 lng</label>
              <input type="number" step="0.0001" value={lng} onChange={(e) => setLng(Number(e.target.value) || 0)} className={input} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">触发半径（米）</label>
            <input type="number" min={0} step={10} value={radius} onChange={(e) => setRadius(Number(e.target.value) || 0)} className={input} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">触发模式</label>
            <select value={triggerMode} onChange={(e) => setTriggerMode(e.target.value as "circle" | "strip")} className={input}>
              <option value="circle">圆形 (Circle)</option>
              <option value="strip">条带 (Strip)</option>
            </select>
          </div>
          {triggerMode === "strip" && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">朝向角（度）</label>
                <input
                  type="number"
                  step="0.01"
                  value={triggerHeadingDeg ?? ""}
                  onChange={(e) => setTriggerHeadingDeg(e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="例如 90"
                  className={input}
                />
                <p className="text-[11px] text-gray-400 mt-1">bearing 0° north / 90° east</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">容忍宽度（米，总宽）</label>
                <input
                  type="number"
                  min={0}
                  step={10}
                  value={triggerWidthM ?? ""}
                  onChange={(e) => setTriggerWidthM(e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="例如 100"
                  className={input}
                />
                <p className="text-[11px] text-gray-400 mt-1">条带总宽，实际左右各取一半</p>
              </div>
            </>
          )}
        </div>
        <div className="border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-2 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 whitespace-nowrap">取消</button>
          <button
            onClick={save}
            disabled={busy || !name.trim() || !dirty}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60 whitespace-nowrap"
          >
            {busy ? "保存中…" : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

// 添加点位"超简版"—— 只填名字，其它字段走后端默认值。
// 先 POST /scenes 创建一个最小 scene；外层再 POST /routes/:id/points 把它挂到当前游线方向。
function QuickAddPointModal({
  routeShortName,
  direction,
  nextSeq,
  onClose,
  onCreated,
}: {
  routeShortName: string;
  direction: Direction;
  nextSeq: number;
  onClose: () => void;
  onCreated: (sceneId: string, name: string) => void | Promise<void>;
}) {
  const [name, setName] = useState("");
  const [isStandard, setIsStandard] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast("请填写点位名称", "error"); return; }
    setBusy(true);
    try {
      const created = await apiSend<Scene>("POST", "/scenes", {
        name: name.trim(),
        // 以下都是默认值；后端 handleCreateScene 会接受不到字段时也设默认（visibility=public、status=active）
        description: "",
        lat: 0,
        lng: 0,
        triggerRadius: 300,
        voice: "",
        isStandard,
        triggerMode: "circle",
        triggerHeadingDeg: null,
        triggerWidthM: null,
      });
      await onCreated(String(created.id), created.name);
    } catch (e) {
      toast(`创建失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h3 className="text-base font-semibold text-gray-900">添加点位</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {routeShortName} · {direction === "up" ? "上行" : "下行"} · 顺序 {nextSeq}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-2">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">点位名称 <span className="text-red-500">*</span></label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="如 三峡大坝"
            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
          <p className="text-xs text-gray-400 mt-2">其它信息（位置、触发半径、音色、讲解词等）可在点位详情或游线表中继续完善。</p>

          <label className="flex items-start gap-2 mt-3 p-3 bg-teal-50/60 border border-teal-200 rounded-lg cursor-pointer hover:bg-teal-50">
            <input
              type="checkbox"
              checked={isStandard}
              onChange={(e) => setIsStandard(e.target.checked)}
              className="mt-0.5 accent-teal-600"
            />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-gray-900">归入标准点位（公共可复用）</div>
              <div className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                勾选后该点位会出现在「点位内容」列表（按上行/下行分组），其它游线可通过「从点位库导入」复用。
                不勾选则仅本游线私有，列表不展示。
              </div>
            </div>
          </label>
        </div>
        <div className="border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-2 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 whitespace-nowrap">取消</button>
          <button onClick={submit} disabled={busy || !name.trim()} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60 whitespace-nowrap">
            {busy ? "保存中…" : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

function toRad(deg: number) {
  return (deg * Math.PI) / 180;
}

function toDeg(rad: number) {
  return (rad * 180) / Math.PI;
}

function destinationPoint(lat: number, lng: number, bearingDeg: number, distanceM: number): [number, number] {
  const earthRadiusM = 6371000;
  const bearingRad = toRad(bearingDeg);
  const lat1 = toRad(lat);
  const lng1 = toRad(lng);
  const angularDistance = distanceM / earthRadiusM;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
      Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearingRad),
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(lat1),
      Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2),
    );

  return [toDeg(lat2), ((toDeg(lng2) + 540) % 360) - 180];
}

function computeStripCorners(
  lat: number,
  lng: number,
  headingDeg: number,
  lengthM: number,
  widthM: number,
): [number, number][] {
  // Match the field survey reference HTML: triggerWidthM is the total tolerated
  // width, so the polygon expands half of it to each side of the heading line.
  const halfWidthM = widthM / 2;
  const front = destinationPoint(lat, lng, headingDeg, lengthM);
  const back = destinationPoint(lat, lng, (headingDeg + 180) % 360, lengthM);
  const left = (headingDeg - 90 + 360) % 360;
  const right = (headingDeg + 90) % 360;

  return [
    destinationPoint(front[0], front[1], left, halfWidthM),
    destinationPoint(front[0], front[1], right, halfWidthM),
    destinationPoint(back[0], back[1], right, halfWidthM),
    destinationPoint(back[0], back[1], left, halfWidthM),
  ];
}

function computeHeadingArrow(lat: number, lng: number, headingDeg: number, lengthM: number): [number, number][][] {
  const start = destinationPoint(lat, lng, (headingDeg + 180) % 360, Math.max(30, lengthM * 0.7));
  const tip = destinationPoint(lat, lng, headingDeg, Math.max(80, lengthM * 1.6));
  const left = destinationPoint(tip[0], tip[1], (headingDeg + 150) % 360, 35);
  const right = destinationPoint(tip[0], tip[1], (headingDeg + 210) % 360, 35);
  return [[start, tip], [left, tip, right]];
}

function TriggerAreaMapModal({
  routeName,
  currentDirection,
  allPoints,
  sceneById,
  onClose,
}: {
  routeName: string;
  currentDirection: Direction;
  allPoints: RoutePoint[];
  sceneById: Map<string, Scene>;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState<"current" | "up" | "down" | "all">("current");
  const [provider, setProvider] = useState<BaseProvider>("tdt");
  const [map, setMap] = useState<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  const upPoints = useMemo(() => {
    return allPoints.filter((p) => p.direction === "up").sort((a, b) => a.seq - b.seq);
  }, [allPoints]);

  const downPoints = useMemo(() => {
    return allPoints.filter((p) => p.direction === "down").sort((a, b) => a.seq - b.seq);
  }, [allPoints]);

  const filteredPoints = useMemo(() => {
    const targetDir = filter === "current" ? currentDirection : filter;
    if (targetDir === "all") {
      return [...upPoints, ...downPoints];
    }
    return targetDir === "up" ? upPoints : downPoints;
  }, [filter, currentDirection, upPoints, downPoints]);

  const gcj = projectFor(provider);

  useEffect(() => {
    if (!mapContainerRef.current) return;
    const m = newBaseMap(mapContainerRef.current, provider, () => setProvider("amap"));
    m.setView([30.82, 111.0], 9);
    setMap(m);
    return () => {
      m.remove();
      setMap(null);
    };
  }, [provider]);

  useEffect(() => {
    if (!map) return;

    const layersGroup = L.featureGroup().addTo(map);

    const showUp = filter === "up" || filter === "all" || (filter === "current" && currentDirection === "up");
    const showDown = filter === "down" || filter === "all" || (filter === "current" && currentDirection === "down");

    const getCoords = (pts: RoutePoint[]) => {
      const coords: [number, number][] = [];
      pts.forEach((p) => {
        const sc = sceneById.get(p.sceneId);
        if (sc && Number.isFinite(sc.lat) && Number.isFinite(sc.lng) && sc.lat !== 0 && sc.lng !== 0) {
          coords.push(gcj(sc.lat, sc.lng));
        }
      });
      return coords;
    };

    if (showUp && upPoints.length > 0) {
      const coords = getCoords(upPoints);
      if (coords.length > 1) {
        L.polyline(coords, { color: "#0d9488", weight: 3, opacity: 0.8 }).addTo(layersGroup);
      }
    }

    if (showDown && downPoints.length > 0) {
      const coords = getCoords(downPoints);
      if (coords.length > 1) {
        L.polyline(coords, { color: "#d97706", weight: 3, opacity: 0.8 }).addTo(layersGroup);
      }
    }

    const drawPoint = (p: RoutePoint, colorHex: string, bgClass: string) => {
      const sc = sceneById.get(p.sceneId);
      if (!sc || !Number.isFinite(sc.lat) || !Number.isFinite(sc.lng) || sc.lat === 0 || sc.lng === 0) return;

      const [glat, glng] = gcj(sc.lat, sc.lng);

      const seqIcon = L.divIcon({
        html: `<div class="flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold border border-white shadow-md ${bgClass}">${p.seq}</div>`,
        className: "",
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });

      const marker = L.marker([glat, glng], { icon: seqIcon }).addTo(layersGroup);

      const isStrip = sc.triggerMode === "strip";
      const isValidStrip =
        isStrip &&
        Number.isFinite(sc.triggerHeadingDeg) &&
        sc.triggerHeadingDeg !== null &&
        Number.isFinite(sc.triggerWidthM) &&
        sc.triggerWidthM !== null &&
        sc.triggerWidthM > 0;

      const triggerDesc = isValidStrip
        ? `条带: 沿向±${sc.triggerRadius}m, 总宽${sc.triggerWidthM}m, 航向${sc.triggerHeadingDeg}°`
        : `圆形: 半径${sc.triggerRadius}m`;

      const popupContent = `
        <div class="p-2 space-y-1 min-w-[180px]">
          <div class="font-bold text-gray-900 text-sm">#${p.seq} ${sc.name}</div>
          <div class="text-xs text-gray-500 font-medium">方向: ${p.direction === "up" ? "上行" : "下行"}</div>
          <div class="text-[11px] text-gray-400 font-mono">坐标: ${sc.lat.toFixed(6)}, ${sc.lng.toFixed(6)}</div>
          <div class="text-xs text-gray-700 font-medium pt-1 border-t border-gray-100">触发区: ${triggerDesc}</div>
        </div>
      `;

      marker.bindTooltip(`#${p.seq} ${sc.name}`, { direction: "top", offset: [0, -10] });
      marker.bindPopup(popupContent);

      if (isValidStrip) {
        const corners = computeStripCorners(
          sc.lat,
          sc.lng,
          sc.triggerHeadingDeg!,
          sc.triggerRadius,
          sc.triggerWidthM!,
        );
        const projectedCorners = corners.map(([clat, clng]) => gcj(clat, clng));
        L.polygon(projectedCorners, {
          color: colorHex,
          fillColor: colorHex,
          fillOpacity: 0.15,
          weight: 1.5,
          dashArray: "5, 5",
        }).addTo(layersGroup);
        const [shaft, head] = computeHeadingArrow(sc.lat, sc.lng, sc.triggerHeadingDeg!, sc.triggerRadius).map((line) =>
          line.map(([alat, alng]) => gcj(alat, alng)),
        );
        L.polyline(shaft, { color: "#111827", weight: 2.5, opacity: 0.85 }).addTo(layersGroup);
        L.polyline(head, { color: "#111827", weight: 2.5, opacity: 0.85 }).addTo(layersGroup);
      } else {
        L.circle([glat, glng], {
          radius: sc.triggerRadius,
          color: colorHex,
          fillColor: colorHex,
          fillOpacity: 0.15,
          weight: 1.5,
        }).addTo(layersGroup);
      }
    };

    if (showUp) {
      upPoints.forEach((p) => drawPoint(p, "#0d9488", "bg-teal-600"));
    }
    if (showDown) {
      downPoints.forEach((p) => drawPoint(p, "#d97706", "bg-amber-600"));
    }

    const bounds = layersGroup.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }

    return () => {
      layersGroup.remove();
    };
  }, [map, provider, filter, upPoints, downPoints, currentDirection, sceneById]);

  const currentDirectionLabel = currentDirection === "up" ? "上行" : "下行";

  const filters: { key: typeof filter; label: string }[] = [
    { key: "current", label: `当前方向 (${currentDirectionLabel})` },
    { key: "up", label: "上行" },
    { key: "down", label: "下行" },
    { key: "all", label: "全部" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-[90vw] h-[85vh] bg-white rounded-xl shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-3.5 flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                <i className="ri-map-2-line text-teal-600 mr-1.5" />
                {routeName} · 触发区域地图
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">可视化并验证点位在地图上的触发区域</p>
            </div>
            {/* Filter Buttons */}
            <div className="bg-gray-100 p-0.5 rounded-lg flex items-center gap-0.5 border border-gray-200">
              {filters.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                    filter === f.key
                      ? "bg-white text-gray-900 shadow-sm border border-gray-200/50"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50/50"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {/* Map Provider Selector */}
            <div className="bg-gray-100 p-0.5 rounded-lg flex items-center gap-0.5 border border-gray-200">
              <button
                onClick={() => setProvider("tdt")}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                  provider === "tdt"
                    ? "bg-white text-gray-900 shadow-sm border border-gray-200/50"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50/50"
                }`}
              >
                天地图
              </button>
              <button
                onClick={() => setProvider("amap")}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                  provider === "amap"
                    ? "bg-white text-gray-900 shadow-sm border border-gray-200/50"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50/50"
                }`}
              >
                高德地图
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-gray-500 text-xl" />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Map */}
          <div ref={mapContainerRef} className="flex-1 h-full relative bg-gray-100" />

          {/* Sidebar */}
          <div className="w-72 bg-gray-50 border-l border-gray-200 flex flex-col shrink-0">
            <div className="p-3 border-b border-gray-200 bg-white">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                点位列表 ({filteredPoints.length})
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {filteredPoints.length === 0 ? (
                <div className="py-8 text-center text-xs text-gray-400">当前筛选方向暂无点位</div>
              ) : (
                filteredPoints.map((p) => {
                  const sc = sceneById.get(p.sceneId);
                  if (!sc || !Number.isFinite(sc.lat) || !Number.isFinite(sc.lng) || sc.lat === 0 || sc.lng === 0)
                    return null;
                  const isUp = p.direction === "up";
                  return (
                    <button
                      key={`${p.direction}-${p.seq}`}
                      onClick={() => {
                        if (map) {
                          const [glat, glng] = gcj(sc.lat, sc.lng);
                          map.setView([glat, glng], 16);
                        }
                      }}
                      className="w-full text-left p-2 hover:bg-teal-50/60 rounded transition-colors flex items-start gap-2 border border-transparent hover:border-teal-100"
                    >
                      <span
                        className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-bold shrink-0 ${
                          isUp ? "bg-teal-600" : "bg-amber-600"
                        }`}
                      >
                        {p.seq}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-gray-900 truncate">{sc.name}</div>
                        <div className="text-[10px] text-gray-500 truncate mt-0.5">
                          {sc.triggerMode === "strip"
                            ? `条带: 沿±${sc.triggerRadius}m/宽${sc.triggerWidthM}m`
                            : `圆形: ${sc.triggerRadius}m`}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Legend & Info Note */}
            <div className="p-4 border-t border-gray-200 bg-white space-y-4 text-xs shrink-0">
              <div>
                <div className="font-semibold text-gray-700 mb-2">图例</div>
                <div className="grid grid-cols-2 gap-2 text-gray-600 font-medium">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3.5 h-0.5 bg-[#0d9488] inline-block rounded" />
                    <span>上行路径</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3.5 h-0.5 bg-[#d97706] inline-block rounded" />
                    <span>下行路径</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full bg-teal-600 flex items-center justify-center text-[9px] text-white font-bold">1</span>
                    <span>上行点位</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full bg-amber-600 flex items-center justify-center text-[9px] text-white font-bold">2</span>
                    <span>下行点位</span>
                  </div>
                  <div className="flex items-center gap-1.5 col-span-2">
                    <span className="w-4 h-4 rounded-full border border-teal-600/50 bg-teal-500/15 inline-block shrink-0" />
                    <span>圆形触发区</span>
                  </div>
                  <div className="flex items-center gap-1.5 col-span-2">
                    <span className="w-5 h-3 border border-dashed border-teal-600/50 bg-teal-500/15 inline-block shrink-0" />
                    <span>条带触发区</span>
                  </div>
                  <div className="flex items-center gap-1.5 col-span-2">
                    <span className="w-5 h-0.5 bg-gray-900 inline-block shrink-0" />
                    <span>黑色线为航向角方向</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-3">
                <div className="font-semibold text-gray-700 mb-1">条带触发区计算说明</div>
                <p className="text-[10px] text-gray-500 leading-relaxed">
                  <strong>朝向角</strong>：0°为正北，90°为正东。<br />
                  <strong>沿向长度</strong>：即 triggerRadius，表示沿朝向前后各延伸的距离（总长 2×Radius）。<br />
                  <strong>侧向宽度</strong>：即 triggerWidthM，表示垂直于航向的总宽，绘制时左右各取一半。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RouteDetailPage;
